import { useState, useEffect } from 'react';
import { fetchAPI, UrlParams } from '../services/strapi';

function mapResponse(result: any, urlPrefix = "") {
  const dateList = `${result.attributes.data_defesa}`.split('-');
  return {
    title: result.attributes.title,
    author: result.attributes.author,
    date: dateList.length === 3? `${dateList[0]}` : result.attributes.data_defesa,
    pdf_url: result.attributes.url_file
  };
}

export enum level {
  EMPTY="",
  MESTRADO="Mestrado",
  DOUTORADO="Doutorado"
}

export type Search = {
  buscaLivre?: string;
  author?: string;
  initialDate?: string;
  finalDate?: string;
  orientador?: string;
  programa_posgraduacao?: string;
  instituicao?: string;
  nivel?: level;
  indice?: string;
}

type apiResponse = {
  data: any[],
  meta: {
		pagination: {
			page: number,
			pageSize: number,
			pageCount: number,
			total: number
		}
	}
}

export type Response = {
  results: any[],
  pagination: {
    page: number,
    pageSize: number,
    pageCount: number,
    total: number
	}
}

function mountFilter(array: any[], field: string | undefined | null, fieldTitle: string, operator: string = "$containsi") {
  if(field) {
    let filter: any = {};
    filter[fieldTitle] = {};
    filter[fieldTitle][operator] = field;

    array.push(filter);
  }
}

function mountCaseSensitiveFilter(array: any[], field: string | undefined | null, fieldTitle: string, operator: string = "$containsi") {
  if(field) {
    const tempArray: any[] = [];
    mountFilter(tempArray, field, fieldTitle, operator);
    mountFilter(tempArray, `${field}`.toUpperCase(), fieldTitle, operator);
    mountFilter(tempArray, `${field}`.toLowerCase(), fieldTitle, operator);

    array.push({
      $or: tempArray
    })
  }
}

function mapFilter(search: Search) {
  let filters: any = {};

  let or: any[] = [];
  if(search.buscaLivre) {
    mountCaseSensitiveFilter(or, search.buscaLivre, 'search_text');
    mountCaseSensitiveFilter(or, search.buscaLivre, 'title');
    mountCaseSensitiveFilter(or, search.buscaLivre, 'author');
    mountCaseSensitiveFilter(or, search.buscaLivre, 'orientador');
    mountCaseSensitiveFilter(or, search.buscaLivre, 'programa_posgraduacao');
    mountCaseSensitiveFilter(or, search.buscaLivre, 'instituicao');
    mountCaseSensitiveFilter(or, search.buscaLivre, 'indice');
    
    filters.$or = or;
  }

  let and: any[] = []
  mountFilter(and, search.initialDate, 'data_defesa', '$gte');
  mountFilter(and, search.finalDate, 'data_defesa', '$lte');

  mountCaseSensitiveFilter(and, search.author, 'author');
  mountCaseSensitiveFilter(and, search.orientador, 'orientador');
  mountCaseSensitiveFilter(and, search.programa_posgraduacao, 'programa_posgraduacao');
  mountCaseSensitiveFilter(and, search.instituicao, 'instituicao');
  mountFilter(and, search.indice, 'indice');
  mountFilter(and, search.nivel, 'nivel');

  filters.$and = and;

  return filters
}

export function mapInitialSearch(_level: string) {
  switch (_level) {
    case "teses-doutorado":
      return level.DOUTORADO
    case "dissertacoes-mestrado":
      return level.MESTRADO
    
    default:
      return level.EMPTY
  }
}

export function useDissertations(initialSearch: string = ''): Response {
  const initialData: Response = {
    results: [], 
    pagination: {
      page: -1,
      pageCount: -1,
      pageSize: -1,
      total: -1
    }
  }
  const [data, setData] = useState<Response>(initialData);
  
  async function fetchData() {
    let query: UrlParams = {
      populate: "*",
      pagination: {
        page: 1,
        pageSize: 10
      },
      sort: ['data_defesa:desc', 'title:asc'],
    };

    if (mapInitialSearch(initialSearch)) {
      query.filters = {
        nivel: {
          $containsi: mapInitialSearch(initialSearch)
        }
      }
    }

    setData(await makeRequest(query));
  }

  useEffect(() => {
    //if(data?.results?.length) return;
    fetchData();
  }, [data.pagination.page, initialSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  return data;
};

export const orderObjects = [
  {
    frontValue: 'Mais Recentes',
    backValue: ['data_defesa:desc', 'title:desc']
  },
  {
    frontValue: 'Mais Antigas',
    backValue: ['data_defesa:asc', 'title:asc']
  },
  {
    frontValue: 'Alfabética A - Z',
    backValue: ['title:asc', 'data_defesa:asc']
  },
  {
    frontValue: 'Alfabética Z - A',
    backValue: ['title:desc', 'data_defesa:desc']
  }
]

export async function getDissertations(search: Search | null = null,  page: number = 1, order: string[] = orderObjects[0].backValue): Promise<Response> {
  let query: UrlParams = {
    populate: "*",
    pagination: {
      page: page,
      pageSize: 10
    },
    sort: order,
  };
  if(search) {
    query.filters = mapFilter(search);
  }
  return await makeRequest(query);
}


async function makeRequest(query: any): Promise<Response> {
  const response: apiResponse = await fetchAPI("/dissertations", query);
    return {
      results: response.data.map((item: any) => mapResponse(item)),
      pagination: response.meta.pagination
    };
}